import { useTranslation } from "react-i18next";
import * as sup from "superstruct";

import { Form } from "@/modules/form/components/Form/Form";
import { PasswordInput } from "@/modules/form/components/PasswordInput/PasswordInput";
import {
  areEqual,
  notEmpty,
  passwordPattern,
} from "@/modules/form/utils/structs";
import { Button } from "@/modules/theme/components/Button/Button";
import { useTheme } from "@/modules/theme/hooks/useTheme";
import { recompose } from "@/utils/structs/recompose";

import { createChangePasswordFormStyles } from "./createChangePasswordFormStyles";

const changeResetPasswordFormStruct = sup.object({
  newPassword: recompose(sup.string(), notEmpty, passwordPattern),
  repeatedPassword: recompose(sup.string(), notEmpty, areEqual("newPassword")),
});

const translationNamespace = "common/ChangePasswordForm";

type ChangePasswordFormProps = {
  isLoading: boolean;
  mode: "reset" | "setup";
  onValidSubmit: ({ newPassword }: { newPassword: string }) => void;
};

export const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({
  isLoading,
  mode,
  onValidSubmit,
}) => {
  const classes = createChangePasswordFormStyles(useTheme());
  const { t } = useTranslation(translationNamespace);
  return (
    <Form
      defaultValues={{
        newPassword: "",
        repeatedPassword: "",
      }}
      struct={changeResetPasswordFormStruct}
      onValidSubmit={onValidSubmit}
    >
      {({ register, trigger }) => {
        const passwordInput = register("newPassword");

        return (
          <div className={classes.formContentWrapper}>
            <PasswordInput
              label={t("Password")}
              {...passwordInput}
              placeholder={t("Enter the password")}
              onChange={async (e) => {
                // `passwordInput.onChange` is a Promise, it waits for password value to be updated before triggering validation on repeatedPassword
                await passwordInput.onChange(e);
                return trigger("repeatedPassword");
              }}
              errorMessages={{
                "string.notEmpty": t("Password is required"),
                "string.passwordPattern": t(
                  "Password should be at least 6 characters long, have min. 1 uppercase letter, 1 digit and 1 special character"
                ),
              }}
            />
            <PasswordInput
              label={t("Password confirmation")}
              {...register("repeatedPassword")}
              placeholder={t("Repeat password")}
              errorMessages={{
                "string.notEmpty": t("Password confirmation is required"),
                "string.areEqual": t("Passwords should match"),
              }}
            />
            <div className={classes.buttonWrapper}>
              <Button
                type="submit"
                isDisabled={isLoading}
                isLoading={isLoading}
                className={classes.button}
                withShadow
              >
                {mode === "reset" ? t("Reset") : t("Set up")}
              </Button>
            </div>
          </div>
        );
      }}
    </Form>
  );
};
